import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * RAG (Retrieval Augmented Generation) Page Component
 *
 * Configure RAG settings and manage product indexing
 *
 * @package WooAIChatbot
 */
import { useState, useEffect, useCallback } from 'react';
import { Database, RefreshCw, Play, Pause, CheckCircle, AlertCircle, Save, Info, Loader2, Link, Key, Table, Zap, Ban, Terminal, Copy, GitMerge } from 'lucide-react';
import { useSettings } from '../../contexts/SettingsContext';
import { useTranslation } from '../../hooks/useTranslation';
import { ProductSelector } from '../components/ProductSelector';
import CouponSelector from '../components/CouponSelector/CouponSelector';
import { ragExclusionsApi, couponExclusionsApi } from '../../utils/api';
// Inline styles matching AIProviders pattern
const styles = {
    page: {
        padding: '32px',
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
        maxWidth: '832px',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen-Sans, Ubuntu, Cantarell, "Helvetica Neue", sans-serif',
    },
    header: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
    },
    title: {
        fontSize: '24px',
        fontWeight: 500,
        color: '#111827',
        margin: 0,
    },
    subtitle: {
        fontSize: '14px',
        color: '#6b7280',
        margin: 0,
    },
    link: {
        color: '#6366f1',
        textDecoration: 'underline',
    },
    card: {
        backgroundColor: '#ffffff',
        borderRadius: '14px',
        border: '1px solid rgba(0,0,0,0.1)',
        padding: '24px',
    },
    cardTitle: {
        fontSize: '16px',
        fontWeight: 500,
        color: '#111827',
        margin: '0 0 20px 0',
    },
    cardDescription: {
        fontSize: '14px',
        color: '#6b7280',
        margin: '0 0 24px 0',
    },
    fieldGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
    },
    field: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
    },
    label: {
        fontSize: '14px',
        fontWeight: 500,
        color: '#374151',
    },
    select: {
        width: '100%',
        height: '40px',
        padding: '0 12px',
        borderRadius: '10px',
        border: '1px solid #d1d5db',
        fontSize: '14px',
        backgroundColor: '#ffffff',
        color: '#111827',
        cursor: 'pointer',
        appearance: 'none',
        backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'none\' viewBox=\'0 0 24 24\' stroke=\'%236b7280\'%3E%3Cpath stroke-linecap=\'round\' stroke-linejoin=\'round\' stroke-width=\'2\' d=\'M19 9l-7 7-7-7\'%3E%3C/path%3E%3C/svg%3E")',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'right 12px center',
        backgroundSize: '16px',
        paddingRight: '40px',
    },
    input: {
        width: '100%',
        height: '36px',
        padding: '0 12px',
        borderRadius: '8px',
        border: '1px solid #d1d5db',
        fontSize: '14px',
        backgroundColor: '#f3f3f5',
        color: '#111827',
        boxSizing: 'border-box',
    },
    hint: {
        fontSize: '12px',
        color: '#9ca3af',
        margin: 0,
    },
    toggleRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '16px 0',
        borderBottom: '1px solid #e5e7eb',
    },
    toggleLabel: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
    },
    toggleTitle: {
        fontSize: '14px',
        fontWeight: 500,
        color: '#374151',
    },
    toggleDescription: {
        fontSize: '12px',
        color: '#9ca3af',
    },
    toggle: {
        width: '44px',
        height: '24px',
        borderRadius: '12px',
        border: 'none',
        cursor: 'pointer',
        position: 'relative',
        transition: 'background-color 0.2s ease',
    },
    toggleEnabled: {
        backgroundColor: '#6366f1',
    },
    toggleDisabled: {
        backgroundColor: '#d1d5db',
    },
    toggleKnob: {
        position: 'absolute',
        top: '2px',
        width: '20px',
        height: '20px',
        borderRadius: '50%',
        backgroundColor: '#ffffff',
        transition: 'left 0.2s ease',
        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
    },
    statsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: '16px',
        marginBottom: '24px',
    },
    statCard: {
        backgroundColor: '#f9fafb',
        borderRadius: '10px',
        padding: '16px',
        textAlign: 'center',
    },
    statValue: {
        fontSize: '24px',
        fontWeight: 600,
        color: '#111827',
        margin: 0,
    },
    statLabel: {
        fontSize: '12px',
        color: '#6b7280',
        margin: '4px 0 0 0',
    },
    progressContainer: {
        marginBottom: '24px',
    },
    progressHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '8px',
    },
    progressLabel: {
        fontSize: '14px',
        fontWeight: 500,
        color: '#374151',
    },
    progressPercent: {
        fontSize: '14px',
        fontWeight: 500,
        color: '#6366f1',
    },
    progressBar: {
        width: '100%',
        height: '8px',
        backgroundColor: '#e5e7eb',
        borderRadius: '4px',
        overflow: 'hidden',
    },
    progressFill: {
        height: '100%',
        backgroundColor: '#6366f1',
        borderRadius: '4px',
        transition: 'width 0.3s ease',
    },
    buttonRow: {
        display: 'flex',
        gap: '12px',
    },
    primaryButton: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        height: '40px',
        padding: '0 20px',
        borderRadius: '8px',
        border: 'none',
        backgroundColor: '#6366f1',
        color: '#ffffff',
        fontSize: '14px',
        fontWeight: 500,
        cursor: 'pointer',
    },
    primaryButtonDisabled: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        height: '40px',
        padding: '0 20px',
        borderRadius: '8px',
        border: 'none',
        backgroundColor: '#6366f1',
        color: '#ffffff',
        fontSize: '14px',
        fontWeight: 500,
        cursor: 'not-allowed',
        opacity: 0.5,
    },
    secondaryButton: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        height: '40px',
        padding: '0 20px',
        borderRadius: '8px',
        border: '1px solid #d1d5db',
        backgroundColor: '#ffffff',
        color: '#374151',
        fontSize: '14px',
        fontWeight: 500,
        cursor: 'pointer',
    },
    dangerButton: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        height: '40px',
        padding: '0 20px',
        borderRadius: '8px',
        border: '1px solid #fecaca',
        backgroundColor: '#fef2f2',
        color: '#dc2626',
        fontSize: '14px',
        fontWeight: 500,
        cursor: 'pointer',
    },
    alert: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
        padding: '16px',
        borderRadius: '8px',
    },
    alertInfo: {
        backgroundColor: '#eff6ff',
        border: '1px solid #bfdbfe',
    },
    alertError: {
        backgroundColor: '#fef2f2',
        border: '1px solid #fecaca',
    },
    alertSuccess: {
        backgroundColor: '#f0fdf4',
        border: '1px solid #bbf7d0',
    },
    alertIcon: {
        width: '20px',
        height: '20px',
        flexShrink: 0,
        marginTop: '2px',
    },
    alertContent: {
        flex: 1,
    },
    alertTitle: {
        fontWeight: 500,
        margin: 0,
    },
    alertMessage: {
        fontSize: '14px',
        marginTop: '4px',
    },
    footer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: '12px',
        paddingTop: '4px',
        borderTop: '1px solid #e5e7eb',
    },
    cancelButton: {
        height: '36px',
        padding: '0 16px',
        borderRadius: '8px',
        border: '1px solid #d1d5db',
        backgroundColor: '#ffffff',
        color: '#374151',
        fontSize: '14px',
        fontWeight: 500,
        cursor: 'pointer',
    },
    saveButton: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        height: '36px',
        padding: '0 16px',
        borderRadius: '8px',
        border: 'none',
        backgroundColor: '#6366f1',
        color: '#ffffff',
        fontSize: '14px',
        fontWeight: 500,
        cursor: 'pointer',
    },
    saveButtonDisabled: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        height: '36px',
        padding: '0 16px',
        borderRadius: '8px',
        border: 'none',
        backgroundColor: '#6366f1',
        color: '#ffffff',
        fontSize: '14px',
        fontWeight: 500,
        cursor: 'not-allowed',
        opacity: 0.5,
    },
    spinner: {
        animation: 'spin 1s linear infinite',
    },
    loading: {
        padding: '32px',
    },
    loadingContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '256px',
    },
    loadingContent: {
        textAlign: 'center',
    },
    loadingSpinner: {
        width: '48px',
        height: '48px',
        border: '2px solid transparent',
        borderBottomColor: '#6366f1',
        borderRadius: '50%',
        margin: '0 auto 16px',
        animation: 'spin 1s linear infinite',
    },
};
// Embedding models available
const EMBEDDING_MODELS = [
    { value: 'gemini-embedding-2-preview', label: 'Google Gemini Embedding 2 (Multimodal - Images + Text)', provider: 'gemini-v2' },
    { value: 'gemini-embedding-001', label: 'Google gemini-embedding-001 (Text only)', provider: 'gemini' },
    { value: 'text-embedding-3-small', label: 'OpenAI text-embedding-3-small', provider: 'openai' },
    { value: 'text-embedding-3-large', label: 'OpenAI text-embedding-3-large', provider: 'openai' },
    { value: 'text-embedding-ada-002', label: 'OpenAI Ada 002 (Legacy)', provider: 'openai' },
];
export function RAG() {
    const { settings, updateSettings, loading } = useSettings();
    const { t } = useTranslation();
    // RAG Settings state
    const [ragEnabled, setRagEnabled] = useState(false);
    const [embeddingModel, setEmbeddingModel] = useState('gemini-embedding-001');
    const [chunkSize, setChunkSize] = useState(500);
    const [chunkOverlap, setChunkOverlap] = useState(100);
    const [topK, setTopK] = useState(5);
    const [similarityThreshold, setSimilarityThreshold] = useState(0.7);
    // Supabase settings state
    const [supabaseUrl, setSupabaseUrl] = useState('');
    const [supabaseApiKey, setSupabaseApiKey] = useState('');
    const [supabaseTableName, setSupabaseTableName] = useState('product_embeddings');
    const [supabaseConnectionString, setSupabaseConnectionString] = useState('');
    const [testingConnection, setTestingConnection] = useState(false);
    const [connectionStatus, setConnectionStatus] = useState('idle');
    const [connectionMessage, setConnectionMessage] = useState('');
    // Migration state
    const [schemaStatus, setSchemaStatus] = useState('unknown');
    const [migrating, setMigrating] = useState(false);
    const [migrationMessage, setMigrationMessage] = useState('');
    const [migrationSuccess, setMigrationSuccess] = useState(null);
    const [showMigrationSql, setShowMigrationSql] = useState(false);
    const [migrationSql, setMigrationSql] = useState('');
    const [sqlCopied, setSqlCopied] = useState(false);
    // Indexing state
    const [indexingStatus, setIndexingStatus] = useState({
        isIndexing: false,
        totalProducts: 0,
        indexedProducts: 0,
        lastIndexedAt: null,
        vectorCount: 0,
        status: 'idle',
    });
    // UI state
    const [saving, setSaving] = useState(false);
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [error, setError] = useState(null);
    // Excluded products state
    const [excludedProductIds, setExcludedProductIds] = useState([]);
    const [loadingExcluded, setLoadingExcluded] = useState(false);
    // Content indexing toggles
    const [indexPages, setIndexPages] = useState(false);
    const [indexPosts, setIndexPosts] = useState(false);
    const [indexingPages, setIndexingPages] = useState(false);
    const [indexingPosts, setIndexingPosts] = useState(false);
    // Excluded coupons state
    const [excludedCouponIds, setExcludedCouponIds] = useState([]);
    const [loadingExcludedCoupons, setLoadingExcludedCoupons] = useState(false);
    // Load settings
    useEffect(() => {
        if (settings?.rag) {
            setRagEnabled(settings.rag.enabled ?? false);
            setEmbeddingModel(settings.rag.embeddingModel ?? 'gemini-embedding-001');
            setChunkSize(settings.rag.chunkSize ?? 500);
            setChunkOverlap(settings.rag.chunkOverlap ?? 100);
            setTopK(settings.rag.topK ?? 5);
            setSimilarityThreshold(settings.rag.similarityThreshold ?? 0.7);
            // Supabase settings
            setSupabaseUrl(settings.rag.supabaseUrl ?? '');
            setSupabaseApiKey(settings.rag.supabaseApiKey ?? '');
            setSupabaseTableName(settings.rag.supabaseTableName ?? 'product_embeddings');
            setSupabaseConnectionString(settings.rag.supabaseConnectionString ?? '');
            // Content indexing toggles
            setIndexPages(settings.rag.indexPages ?? false);
            setIndexPosts(settings.rag.indexPosts ?? false);
        }
    }, [settings]);
    // Load excluded products
    const loadExcludedProducts = useCallback(async () => {
        try {
            setLoadingExcluded(true);
            const ids = await ragExclusionsApi.getAll();
            setExcludedProductIds(ids);
        }
        catch (err) {
            console.error('Failed to load excluded products:', err);
        }
        finally {
            setLoadingExcluded(false);
        }
    }, []);
    useEffect(() => {
        loadExcludedProducts();
    }, [loadExcludedProducts]);
    // Handle excluded products change (add/remove)
    const handleExcludedChange = async (newIds) => {
        try {
            // Find IDs that were added
            const idsToAdd = newIds.filter(id => !excludedProductIds.includes(id));
            // Find IDs that were removed
            const idsToRemove = excludedProductIds.filter(id => !newIds.includes(id));
            // Add new exclusions
            if (idsToAdd.length > 0) {
                await ragExclusionsApi.add(idsToAdd);
            }
            // Remove exclusions
            for (const id of idsToRemove) {
                await ragExclusionsApi.remove(id);
            }
            // Reload if changes were made
            if (idsToAdd.length > 0 || idsToRemove.length > 0) {
                await loadExcludedProducts();
            }
        }
        catch (err) {
            console.error('Failed to update excluded products:', err);
            setError(t('failedToUpdateExcludedProducts'));
        }
    };
    // Load excluded coupons
    const loadExcludedCoupons = useCallback(async () => {
        try {
            setLoadingExcludedCoupons(true);
            const ids = await couponExclusionsApi.getAll();
            setExcludedCouponIds(ids);
        }
        catch (err) {
            console.error('Failed to load excluded coupons:', err);
        }
        finally {
            setLoadingExcludedCoupons(false);
        }
    }, []);
    useEffect(() => {
        loadExcludedCoupons();
    }, [loadExcludedCoupons]);
    // Handle excluded coupons change (add/remove)
    const handleExcludedCouponsChange = async (newIds) => {
        try {
            const idsToAdd = newIds.filter(id => !excludedCouponIds.includes(id));
            const idsToRemove = excludedCouponIds.filter(id => !newIds.includes(id));
            if (idsToAdd.length > 0) {
                await couponExclusionsApi.add(idsToAdd);
            }
            for (const id of idsToRemove) {
                await couponExclusionsApi.remove(id);
            }
            if (idsToAdd.length > 0 || idsToRemove.length > 0) {
                await loadExcludedCoupons();
            }
        }
        catch (err) {
            console.error('Failed to update excluded coupons:', err);
        }
    };
    // Fetch indexing status
    const fetchIndexingStatus = useCallback(async () => {
        try {
            const globals = window.wooAiChatbot ?? window.wooAIChatbot;
            // Use hardcoded /wp-json to avoid string replacement issues
            const restUrl = '/wp-json';
            const response = await fetch(`${restUrl}/woo-ai/v1/rag/status`, {
                headers: {
                    'X-WP-Nonce': globals?.nonce || '',
                },
            });
            if (response.ok) {
                const data = await response.json();
                setIndexingStatus(data);
            }
        }
        catch (err) {
            console.error('Failed to fetch indexing status:', err);
        }
    }, []);
    // Initial status fetch
    useEffect(() => {
        fetchIndexingStatus();
        // Poll while indexing
        const interval = setInterval(() => {
            if (indexingStatus.isIndexing) {
                fetchIndexingStatus();
            }
        }, 2000);
        return () => clearInterval(interval);
    }, [fetchIndexingStatus, indexingStatus.isIndexing]);
    // Start indexing
    const handleStartIndexing = async () => {
        try {
            setError(null);
            const globals = window.wooAiChatbot ?? window.wooAIChatbot;
            // Use hardcoded /wp-json to avoid string replacement issues
            const restUrl = '/wp-json';
            const response = await fetch(`${restUrl}/woo-ai/v1/rag/index`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-WP-Nonce': globals?.nonce || '',
                },
                body: JSON.stringify({ action: 'start' }),
            });
            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.message || t('failedToStartIndexing'));
            }
            setIndexingStatus(prev => ({ ...prev, isIndexing: true, status: 'indexing' }));
            fetchIndexingStatus();
        }
        catch (err) {
            setError(err.message);
        }
    };
    // Stop indexing
    const handleStopIndexing = async () => {
        try {
            const globals = window.wooAiChatbot ?? window.wooAIChatbot;
            // Use hardcoded /wp-json to avoid string replacement issues
            const restUrl = '/wp-json';
            await fetch(`${restUrl}/woo-ai/v1/rag/index`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-WP-Nonce': globals?.nonce || '',
                },
                body: JSON.stringify({ action: 'stop' }),
            });
            setIndexingStatus(prev => ({ ...prev, isIndexing: false, status: 'idle' }));
        }
        catch (err) {
            setError(err.message);
        }
    };
    // Clear index
    const handleClearIndex = async () => {
        if (!confirm(t('confirmClearIndex'))) {
            return;
        }
        try {
            setError(null);
            const globals = window.wooAiChatbot ?? window.wooAIChatbot;
            // Use hardcoded /wp-json to avoid string replacement issues
            const restUrl = '/wp-json';
            const response = await fetch(`${restUrl}/woo-ai/v1/rag/index`, {
                method: 'DELETE',
                headers: {
                    'X-WP-Nonce': globals?.nonce || '',
                },
            });
            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.message || t('failedToClearIndex'));
            }
            setIndexingStatus({
                isIndexing: false,
                totalProducts: indexingStatus.totalProducts,
                indexedProducts: 0,
                lastIndexedAt: null,
                vectorCount: 0,
                status: 'idle',
            });
        }
        catch (err) {
            setError(err.message);
        }
    };
    // Test Supabase connection
    const handleTestConnection = async () => {
        if (!supabaseUrl || !supabaseApiKey) {
            setConnectionStatus('error');
            setConnectionMessage(t('enterSupabaseCredentials'));
            return;
        }
        try {
            setTestingConnection(true);
            setConnectionStatus('idle');
            setConnectionMessage('');
            setError(null);
            const globals = window.wooAiChatbot ?? window.wooAIChatbot;
            // Use hardcoded /wp-json to avoid string replacement issues
            const restUrl = '/wp-json';
            const response = await fetch(`${restUrl}/woo-ai/v1/rag/test-connection`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-WP-Nonce': globals?.nonce || '',
                },
                body: JSON.stringify({
                    supabaseUrl,
                    supabaseApiKey,
                    supabaseTableName,
                }),
            });
            const data = await response.json();
            if (response.ok && data.success) {
                setConnectionStatus('success');
                setConnectionMessage(data.message || t('connectionSuccessful'));
            }
            else {
                setConnectionStatus('error');
                setConnectionMessage(data.message || t('connectionFailed'));
            }
        }
        catch (err) {
            setConnectionStatus('error');
            setConnectionMessage(err.message);
        }
        finally {
            setTestingConnection(false);
        }
    };
    // Check schema status
    const handleCheckSchema = async () => {
        if (!supabaseConnectionString) {
            setSchemaStatus('unknown');
            return;
        }
        try {
            const globals = window.wooAiChatbot ?? window.wooAIChatbot;
            const restUrl = '/wp-json';
            const response = await fetch(`${restUrl}/woo-ai/v1/rag/check-schema`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-WP-Nonce': globals?.nonce || '',
                },
                body: JSON.stringify({ connectionString: supabaseConnectionString }),
            });
            const data = await response.json();
            if (!data.pg_available) {
                setSchemaStatus('pg_unavailable');
            }
            else if (data.needs_migration) {
                setSchemaStatus('needs_setup');
            }
            else {
                setSchemaStatus('ready');
            }
        }
        catch {
            setSchemaStatus('unknown');
        }
    };
    // Run migration
    const handleMigrate = async () => {
        if (!supabaseConnectionString) {
            setMigrationMessage('Please enter a database connection string first.');
            setMigrationSuccess(false);
            return;
        }
        try {
            setMigrating(true);
            setMigrationMessage('');
            setMigrationSuccess(null);
            const globals = window.wooAiChatbot ?? window.wooAIChatbot;
            const restUrl = '/wp-json';
            const response = await fetch(`${restUrl}/woo-ai/v1/rag/migrate`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-WP-Nonce': globals?.nonce || '',
                },
                body: JSON.stringify({ connectionString: supabaseConnectionString }),
            });
            const data = await response.json();
            setMigrationSuccess(!!data.success);
            setMigrationMessage(data.message || (data.success ? 'Migration completed.' : 'Migration failed.'));
            if (data.success) {
                setSchemaStatus('ready');
            }
            else if (!data.pg_available) {
                setSchemaStatus('pg_unavailable');
            }
        }
        catch (err) {
            setMigrationSuccess(false);
            setMigrationMessage(err.message);
        }
        finally {
            setMigrating(false);
        }
    };
    // Load migration SQL
    const handleLoadMigrationSql = async () => {
        if (migrationSql) {
            setShowMigrationSql(!showMigrationSql);
            return;
        }
        try {
            const globals = window.wooAiChatbot ?? window.wooAIChatbot;
            const restUrl = '/wp-json';
            const response = await fetch(`${restUrl}/woo-ai/v1/rag/migration-sql`, {
                headers: { 'X-WP-Nonce': globals?.nonce || '' },
            });
            const data = await response.json();
            if (data.sql) {
                setMigrationSql(data.sql);
                setShowMigrationSql(true);
            }
        }
        catch {
            // ignore
        }
    };
    // Copy SQL to clipboard
    const handleCopySql = async () => {
        try {
            await navigator.clipboard.writeText(migrationSql);
            setSqlCopied(true);
            setTimeout(() => setSqlCopied(false), 2000);
        }
        catch {
            // Fallback for older browsers
            const el = document.createElement('textarea');
            el.value = migrationSql;
            document.body.appendChild(el);
            el.select();
            document.execCommand('copy');
            document.body.removeChild(el);
            setSqlCopied(true);
            setTimeout(() => setSqlCopied(false), 2000);
        }
    };
    // Index pages
    const handleIndexPages = async () => {
        try {
            setIndexingPages(true);
            setError(null);
            const globals = window.wooAiChatbot ?? window.wooAIChatbot;
            const restUrl = '/wp-json';
            const response = await fetch(`${restUrl}/woo-ai/v1/rag/index-pages`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-WP-Nonce': globals?.nonce || '',
                },
            });
            const data = await response.json();
            if (!response.ok || !data.success) {
                throw new Error(data.message || 'Failed to index pages');
            }
            fetchIndexingStatus();
        }
        catch (err) {
            setError(err.message);
        }
        finally {
            setIndexingPages(false);
        }
    };
    // Index posts
    const handleIndexPosts = async () => {
        try {
            setIndexingPosts(true);
            setError(null);
            const globals = window.wooAiChatbot ?? window.wooAIChatbot;
            const restUrl = '/wp-json';
            const response = await fetch(`${restUrl}/woo-ai/v1/rag/index-posts`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-WP-Nonce': globals?.nonce || '',
                },
            });
            const data = await response.json();
            if (!response.ok || !data.success) {
                throw new Error(data.message || 'Failed to index posts');
            }
            fetchIndexingStatus();
        }
        catch (err) {
            setError(err.message);
        }
        finally {
            setIndexingPosts(false);
        }
    };
    // Save settings
    const handleSaveSettings = async () => {
        try {
            setSaving(true);
            setSaveSuccess(false);
            setError(null);
            await updateSettings({
                rag: {
                    enabled: ragEnabled,
                    embeddingModel,
                    chunkSize,
                    chunkOverlap,
                    topK,
                    similarityThreshold,
                    supabaseUrl,
                    supabaseApiKey,
                    supabaseTableName,
                    supabaseConnectionString,
                    indexPages,
                    indexPosts,
                }
            });
            setSaveSuccess(true);
            setTimeout(() => setSaveSuccess(false), 3000);
        }
        catch (err) {
            setError(err.message);
        }
        finally {
            setSaving(false);
        }
    };
    // Calculate progress percentage
    const progressPercent = indexingStatus.totalProducts > 0
        ? Math.round((indexingStatus.indexedProducts / indexingStatus.totalProducts) * 100)
        : 0;
    if (loading) {
        return (_jsx("div", { style: styles.loading, children: _jsx("div", { style: styles.loadingContainer, children: _jsxs("div", { style: styles.loadingContent, children: [_jsx("div", { style: styles.loadingSpinner }), _jsx("p", { style: { color: '#6b7280' }, children: t('loadingRagSettings') })] }) }) }));
    }
    return (_jsxs("div", { style: styles.page, children: [_jsxs("div", { style: styles.header, children: [_jsx("h1", { style: styles.title, children: t('ragTitle') }), _jsxs("p", { style: styles.subtitle, children: [t('ragDescription'), ' ', _jsxs("a", { href: `${window.wooAiChatbot?.documentationUrl || ''}/documentation/rag-search`, target: "_blank", rel: "noopener noreferrer", style: styles.link, children: [t('learnMoreRag'), " \u2192"] })] })] }), error && (_jsxs("div", { style: { ...styles.alert, ...styles.alertError }, children: [_jsx(AlertCircle, { style: { ...styles.alertIcon, color: '#dc2626' } }), _jsxs("div", { style: styles.alertContent, children: [_jsx("p", { style: { ...styles.alertTitle, color: '#991b1b' }, children: t('error') }), _jsx("p", { style: { ...styles.alertMessage, color: '#b91c1c' }, children: error })] })] })), saveSuccess && (_jsxs("div", { style: { ...styles.alert, ...styles.alertSuccess }, children: [_jsx(CheckCircle, { style: { ...styles.alertIcon, color: '#16a34a' } }), _jsxs("div", { style: styles.alertContent, children: [_jsx("p", { style: { ...styles.alertTitle, color: '#166534' }, children: t('settingsSaved') }), _jsx("p", { style: { ...styles.alertMessage, color: '#15803d' }, children: t('ragSettingsSavedMessage') })] })] })), _jsx("div", { style: styles.card, children: _jsxs("div", { style: { ...styles.toggleRow, borderBottom: 'none', padding: 0 }, children: [_jsxs("div", { style: styles.toggleLabel, children: [_jsx("span", { style: styles.toggleTitle, children: t('enableRagSearch') }), _jsx("span", { style: styles.toggleDescription, children: t('ragSearchHint') })] }), _jsx("button", { style: {
                                ...styles.toggle,
                                ...(ragEnabled ? styles.toggleEnabled : styles.toggleDisabled),
                            }, onClick: () => setRagEnabled(!ragEnabled), type: "button", children: _jsx("div", { style: {
                                    ...styles.toggleKnob,
                                    left: ragEnabled ? '22px' : '2px',
                                } }) })] }) }), !ragEnabled && (_jsxs("div", { style: { ...styles.alert, ...styles.alertInfo }, children: [_jsx(Info, { style: { ...styles.alertIcon, color: '#2563eb' } }), _jsxs("div", { style: styles.alertContent, children: [_jsx("p", { style: { ...styles.alertTitle, color: '#1e40af' }, children: t('ragDisabled') }), _jsx("p", { style: { ...styles.alertMessage, color: '#1d4ed8' }, children: t('ragDisabledHint') })] })] })), ragEnabled && (_jsxs("div", { style: styles.card, children: [_jsx("h3", { style: styles.cardTitle, children: "Content Indexing" }), _jsx("p", { style: styles.cardDescription, children: "Choose which content types to index. The AI will use this content to answer store-related questions." }), _jsxs("div", { style: styles.toggleRow, children: [_jsxs("div", { style: styles.toggleLabel, children: [_jsx("span", { style: styles.toggleTitle, children: "Index Pages" }), _jsx("span", { style: styles.toggleDescription, children: "Include WordPress pages (About Us, Contact, Shipping Policy, etc.) in the AI knowledge base" })] }), _jsx("button", { style: {
                                    ...styles.toggle,
                                    ...(indexPages ? styles.toggleEnabled : styles.toggleDisabled),
                                }, onClick: () => setIndexPages(!indexPages), type: "button", children: _jsx("div", { style: { ...styles.toggleKnob, left: indexPages ? '22px' : '2px' } }) })] }), _jsxs("div", { style: { ...styles.toggleRow, borderBottom: 'none' }, children: [_jsxs("div", { style: styles.toggleLabel, children: [_jsx("span", { style: styles.toggleTitle, children: "Index Blog Posts" }), _jsx("span", { style: styles.toggleDescription, children: "Include blog posts in the AI knowledge base for richer context" })] }), _jsx("button", { style: {
                                    ...styles.toggle,
                                    ...(indexPosts ? styles.toggleEnabled : styles.toggleDisabled),
                                }, onClick: () => setIndexPosts(!indexPosts), type: "button", children: _jsx("div", { style: { ...styles.toggleKnob, left: indexPosts ? '22px' : '2px' } }) })] }), (indexPages || indexPosts) && (_jsx("div", { style: { marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #e5e7eb' }, children: _jsxs("div", { style: { display: 'grid', gridTemplateColumns: indexPages && indexPosts ? 'repeat(2, 1fr)' : '1fr', gap: '16px' }, children: [indexPages && (_jsxs("div", { style: styles.statCard, children: [_jsxs("p", { style: styles.statValue, children: [indexingStatus.indexedPages ?? 0, " / ", indexingStatus.totalPages ?? 0] }), _jsx("p", { style: styles.statLabel, children: "Pages Indexed" }), _jsx("button", { style: {
                                                ...(indexingPages ? styles.primaryButtonDisabled : styles.primaryButton),
                                                marginTop: '12px',
                                                width: '100%',
                                                justifyContent: 'center',
                                            }, onClick: handleIndexPages, disabled: indexingPages, children: indexingPages ? (_jsxs(_Fragment, { children: [_jsx(Loader2, { style: { width: '16px', height: '16px', animation: 'spin 1s linear infinite' } }), "Indexing Pages..."] })) : (_jsxs(_Fragment, { children: [_jsx(Play, { style: { width: '16px', height: '16px' } }), "Index Pages"] })) })] })), indexPosts && (_jsxs("div", { style: styles.statCard, children: [_jsxs("p", { style: styles.statValue, children: [indexingStatus.indexedPosts ?? 0, " / ", indexingStatus.totalPosts ?? 0] }), _jsx("p", { style: styles.statLabel, children: "Posts Indexed" }), _jsx("button", { style: {
                                                ...(indexingPosts ? styles.primaryButtonDisabled : styles.primaryButton),
                                                marginTop: '12px',
                                                width: '100%',
                                                justifyContent: 'center',
                                            }, onClick: handleIndexPosts, disabled: indexingPosts, children: indexingPosts ? (_jsxs(_Fragment, { children: [_jsx(Loader2, { style: { width: '16px', height: '16px', animation: 'spin 1s linear infinite' } }), "Indexing Posts..."] })) : (_jsxs(_Fragment, { children: [_jsx(Play, { style: { width: '16px', height: '16px' } }), "Index Posts"] })) })] }))] }) }))] })), _jsxs("div", { style: styles.card, children: [_jsx("h3", { style: styles.cardTitle, children: t('supabaseVectorDb') }), _jsx("p", { style: styles.cardDescription, children: t('supabaseDescription') }), _jsxs("div", { style: styles.fieldGroup, children: [_jsxs("div", { style: styles.field, children: [_jsx("label", { style: styles.label, children: _jsxs("span", { style: { display: 'flex', alignItems: 'center', gap: '6px' }, children: [_jsx(Link, { style: { width: '14px', height: '14px' } }), t('supabaseProjectUrl')] }) }), _jsx("input", { type: "url", value: supabaseUrl, onChange: (e) => setSupabaseUrl(e.target.value), style: styles.input, placeholder: "https://your-project.supabase.co", disabled: !ragEnabled }), _jsx("p", { style: styles.hint, children: t('supabaseUrlHint') })] }), _jsxs("div", { style: styles.field, children: [_jsx("label", { style: styles.label, children: _jsxs("span", { style: { display: 'flex', alignItems: 'center', gap: '6px' }, children: [_jsx(Key, { style: { width: '14px', height: '14px' } }), t('serviceRoleKey')] }) }), _jsx("input", { type: "password", value: supabaseApiKey, onChange: (e) => setSupabaseApiKey(e.target.value), style: styles.input, placeholder: "eyJhbGciOiJIUzI1NiIs...", disabled: !ragEnabled }), _jsx("p", { style: styles.hint, children: t('serviceRoleKeyHint') })] }), _jsxs("div", { style: styles.field, children: [_jsx("label", { style: styles.label, children: _jsxs("span", { style: { display: 'flex', alignItems: 'center', gap: '6px' }, children: [_jsx(Table, { style: { width: '14px', height: '14px' } }), t('tableName')] }) }), _jsx("input", { type: "text", value: supabaseTableName, onChange: (e) => setSupabaseTableName(e.target.value), style: styles.input, placeholder: "product_embeddings", disabled: !ragEnabled }), _jsx("p", { style: styles.hint, children: t('tableNameHint') })] }), _jsxs("div", { style: styles.field, children: [_jsx("label", { style: styles.label, children: _jsxs("span", { style: { display: 'flex', alignItems: 'center', gap: '6px' }, children: [_jsx(Terminal, { style: { width: '14px', height: '14px' } }), "Database Connection String"] }) }), _jsx("input", { type: "password", value: supabaseConnectionString, onChange: (e) => {
                                            setSupabaseConnectionString(e.target.value);
                                            setSchemaStatus('unknown');
                                            setMigrationMessage('');
                                            setMigrationSuccess(null);
                                        }, style: styles.input, placeholder: "postgresql://postgres.[ref]:[password]@aws-0-[region].pooler.supabase.com:6543/postgres", disabled: !ragEnabled }), _jsx("p", { style: styles.hint, children: "Find this in Supabase Dashboard \u2192 Settings \u2192 Database \u2192 Connection string (URI format). Required for automatic schema setup." })] }), ragEnabled && supabaseConnectionString && (_jsxs("div", { style: {
                                    borderTop: '1px solid #e5e7eb',
                                    paddingTop: '16px',
                                    marginTop: '8px',
                                }, children: [_jsxs("div", { style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }, children: [_jsxs("div", { style: { display: 'flex', alignItems: 'center', gap: '8px' }, children: [_jsx("span", { style: { fontSize: '13px', fontWeight: 500, color: '#374151' }, children: "Schema Status:" }), schemaStatus === 'unknown' && (_jsx("span", { style: { fontSize: '13px', color: '#6b7280' }, children: "Not checked yet" })), schemaStatus === 'ready' && (_jsxs("span", { style: { display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px', color: '#16a34a' }, children: [_jsx(CheckCircle, { style: { width: '14px', height: '14px' } }), "Schema is ready"] })), schemaStatus === 'needs_setup' && (_jsxs("span", { style: { display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px', color: '#d97706' }, children: [_jsx(AlertCircle, { style: { width: '14px', height: '14px' } }), "Needs setup"] })), schemaStatus === 'pg_unavailable' && (_jsxs("span", { style: { display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px', color: '#6b7280' }, children: [_jsx(Info, { style: { width: '14px', height: '14px' } }), "Manual setup required"] }))] }), schemaStatus === 'unknown' && (_jsx("button", { style: { ...styles.secondaryButton, fontSize: '12px', padding: '4px 10px' }, onClick: handleCheckSchema, children: "Check Status" }))] }), _jsxs("div", { style: { display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }, children: [schemaStatus !== 'pg_unavailable' && (_jsx("button", { style: schemaStatus === 'ready' ? styles.secondaryButton : styles.primaryButton, onClick: handleMigrate, disabled: migrating, children: migrating ? (_jsxs(_Fragment, { children: [_jsx(Loader2, { style: { width: '16px', height: '16px', animation: 'spin 1s linear infinite' } }), "Setting up..."] })) : (_jsxs(_Fragment, { children: [_jsx(GitMerge, { style: { width: '16px', height: '16px' } }), schemaStatus === 'ready' ? 'Re-run Setup' : 'Auto-Setup Database'] })) })), _jsxs("button", { style: { ...styles.secondaryButton, display: 'flex', alignItems: 'center', gap: '6px' }, onClick: handleLoadMigrationSql, children: [_jsx(Terminal, { style: { width: '14px', height: '14px' } }), showMigrationSql ? 'Hide SQL' : 'Copy SQL'] })] }), migrationMessage && (_jsxs("div", { style: {
                                            marginTop: '10px',
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            gap: '8px',
                                            color: migrationSuccess ? '#16a34a' : '#dc2626',
                                            fontSize: '13px',
                                        }, children: [migrationSuccess
                                                ? _jsx(CheckCircle, { style: { width: '16px', height: '16px', flexShrink: 0, marginTop: '1px' } })
                                                : _jsx(AlertCircle, { style: { width: '16px', height: '16px', flexShrink: 0, marginTop: '1px' } }), _jsx("span", { children: migrationMessage })] })), showMigrationSql && migrationSql && (_jsxs("div", { style: { marginTop: '12px' }, children: [_jsxs("div", { style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }, children: [_jsxs("span", { style: { fontSize: '12px', color: '#6b7280' }, children: ["Paste this SQL into ", _jsx("strong", { children: "Supabase Dashboard \u2192 SQL Editor" }), " and click Run."] }), _jsxs("button", { style: { ...styles.secondaryButton, fontSize: '12px', padding: '4px 10px', display: 'flex', alignItems: 'center', gap: '4px' }, onClick: handleCopySql, children: [_jsx(Copy, { style: { width: '12px', height: '12px' } }), sqlCopied ? 'Copied!' : 'Copy SQL'] })] }), _jsx("pre", { style: {
                                                    background: '#1e1e2e',
                                                    color: '#cdd6f4',
                                                    padding: '12px',
                                                    borderRadius: '6px',
                                                    fontSize: '11px',
                                                    lineHeight: '1.5',
                                                    overflowX: 'auto',
                                                    maxHeight: '300px',
                                                    overflowY: 'auto',
                                                    whiteSpace: 'pre',
                                                    fontFamily: 'monospace',
                                                }, children: migrationSql })] }))] })), _jsxs("div", { style: { display: 'flex', alignItems: 'center', gap: '12px', marginTop: '8px' }, children: [_jsx("button", { style: ragEnabled && supabaseUrl && supabaseApiKey ? styles.secondaryButton : styles.primaryButtonDisabled, onClick: handleTestConnection, disabled: !ragEnabled || !supabaseUrl || !supabaseApiKey || testingConnection, children: testingConnection ? (_jsxs(_Fragment, { children: [_jsx(Loader2, { style: { width: '16px', height: '16px', animation: 'spin 1s linear infinite' } }), t('testing')] })) : (_jsxs(_Fragment, { children: [_jsx(Zap, { style: { width: '16px', height: '16px' } }), t('testConnection')] })) }), connectionStatus === 'success' && (_jsxs("span", { style: { display: 'flex', alignItems: 'center', gap: '6px', color: '#16a34a', fontSize: '14px' }, children: [_jsx(CheckCircle, { style: { width: '16px', height: '16px' } }), connectionMessage] })), connectionStatus === 'error' && (_jsxs("span", { style: { display: 'flex', alignItems: 'center', gap: '6px', color: '#dc2626', fontSize: '14px' }, children: [_jsx(AlertCircle, { style: { width: '16px', height: '16px' } }), connectionMessage] }))] })] })] }), _jsxs("div", { style: styles.card, children: [_jsx("h3", { style: styles.cardTitle, children: t('productIndexing') }), _jsx("p", { style: styles.cardDescription, children: t('productIndexingDescription') }), _jsxs("div", { style: styles.statsGrid, children: [_jsxs("div", { style: styles.statCard, children: [_jsx("p", { style: styles.statValue, children: indexingStatus.totalProducts }), _jsx("p", { style: styles.statLabel, children: t('totalProducts') })] }), _jsxs("div", { style: styles.statCard, children: [_jsx("p", { style: styles.statValue, children: indexingStatus.indexedProducts }), _jsx("p", { style: styles.statLabel, children: t('indexed') })] }), _jsxs("div", { style: styles.statCard, children: [_jsx("p", { style: styles.statValue, children: indexingStatus.vectorCount }), _jsx("p", { style: styles.statLabel, children: t('vectors') })] }), _jsxs("div", { style: styles.statCard, children: [_jsx("p", { style: styles.statValue, children: indexingStatus.lastIndexedAt
                                            ? new Date(indexingStatus.lastIndexedAt).toLocaleDateString()
                                            : '—' }), _jsx("p", { style: styles.statLabel, children: t('lastIndexed') })] })] }), indexingStatus.isIndexing && (_jsxs("div", { style: styles.progressContainer, children: [_jsxs("div", { style: styles.progressHeader, children: [_jsx("span", { style: styles.progressLabel, children: t('indexingProducts') }), _jsxs("span", { style: styles.progressPercent, children: [progressPercent, "%"] })] }), _jsx("div", { style: styles.progressBar, children: _jsx("div", { style: { ...styles.progressFill, width: `${progressPercent}%` } }) })] })), _jsxs("div", { style: styles.buttonRow, children: [indexingStatus.isIndexing ? (_jsxs("button", { style: styles.secondaryButton, onClick: handleStopIndexing, children: [_jsx(Pause, { style: { width: '16px', height: '16px' } }), t('stopIndexing')] })) : (_jsx("button", { style: ragEnabled ? styles.primaryButton : styles.primaryButtonDisabled, onClick: handleStartIndexing, disabled: !ragEnabled, children: indexingStatus.indexedProducts > 0 ? (_jsxs(_Fragment, { children: [_jsx(RefreshCw, { style: { width: '16px', height: '16px' } }), t('reindexProducts')] })) : (_jsxs(_Fragment, { children: [_jsx(Play, { style: { width: '16px', height: '16px' } }), t('startIndexing')] })) })), indexingStatus.indexedProducts > 0 && !indexingStatus.isIndexing && (_jsxs("button", { style: styles.dangerButton, onClick: handleClearIndex, children: [_jsx(Database, { style: { width: '16px', height: '16px' } }), t('clearIndex')] }))] })] }), _jsxs("div", { style: styles.card, children: [_jsxs("h3", { style: { ...styles.cardTitle, display: 'flex', alignItems: 'center', gap: '8px' }, children: [_jsx(Ban, { style: { width: '18px', height: '18px', color: '#dc2626' } }), t('excludedProducts')] }), _jsx("p", { style: styles.cardDescription, children: t('excludedProductsDescription') }), loadingExcluded ? (_jsx("div", { style: { display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '32px' }, children: _jsx(Loader2, { style: { width: '24px', height: '24px', color: '#6366f1', animation: 'spin 1s linear infinite' } }) })) : (_jsx(ProductSelector, { selectedIds: excludedProductIds, onChange: handleExcludedChange, placeholder: t('searchProductsToExclude') })), excludedProductIds.length > 0 && (_jsx("p", { style: { ...styles.hint, marginTop: '12px' }, children: t('excludedProductsCount', { count: excludedProductIds.length }) }))] }), _jsxs("div", { style: styles.card, children: [_jsxs("h3", { style: { ...styles.cardTitle, display: 'flex', alignItems: 'center', gap: '8px' }, children: [_jsx(Ban, { style: { width: '18px', height: '18px', color: '#dc2626' } }), t('excludedCoupons')] }), _jsx("p", { style: styles.cardDescription, children: t('excludedCouponsDescription') }), loadingExcludedCoupons ? (_jsx("div", { style: { display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '32px' }, children: _jsx(Loader2, { style: { width: '24px', height: '24px', color: '#6366f1', animation: 'spin 1s linear infinite' } }) })) : (_jsx(CouponSelector, { selectedIds: excludedCouponIds, onChange: handleExcludedCouponsChange, placeholder: t('searchCouponsToExclude') })), excludedCouponIds.length > 0 && (_jsx("p", { style: { ...styles.hint, marginTop: '12px' }, children: t('excludedCouponsCount', { count: excludedCouponIds.length }) }))] }), _jsxs("div", { style: styles.card, children: [_jsx("h3", { style: styles.cardTitle, children: t('ragSettings') }), _jsxs("div", { style: styles.fieldGroup, children: [_jsxs("div", { style: styles.field, children: [_jsx("label", { style: styles.label, children: t('embeddingModel') }), _jsx("select", { style: styles.select, value: embeddingModel, onChange: (e) => setEmbeddingModel(e.target.value), disabled: !ragEnabled, children: EMBEDDING_MODELS.map(model => (_jsx("option", { value: model.value, children: model.label }, model.value))) }), _jsx("p", { style: styles.hint, children: t('embeddingModelHint') })] }), _jsxs("div", { style: styles.field, children: [_jsx("label", { style: styles.label, children: t('chunkSize') }), _jsx("input", { type: "number", value: chunkSize, onChange: (e) => setChunkSize(parseInt(e.target.value) || 500), style: styles.input, min: 100, max: 2000, disabled: !ragEnabled }), _jsx("p", { style: styles.hint, children: t('chunkSizeHint') })] }), _jsxs("div", { style: styles.field, children: [_jsx("label", { style: styles.label, children: t('chunkOverlap') }), _jsx("input", { type: "number", value: chunkOverlap, onChange: (e) => setChunkOverlap(parseInt(e.target.value) || 50), style: styles.input, min: 0, max: 500, disabled: !ragEnabled }), _jsx("p", { style: styles.hint, children: t('chunkOverlapHint') })] }), _jsxs("div", { style: styles.field, children: [_jsx("label", { style: styles.label, children: t('topKResults') }), _jsx("input", { type: "number", value: topK, onChange: (e) => setTopK(parseInt(e.target.value) || 5), style: styles.input, min: 1, max: 20, disabled: !ragEnabled }), _jsx("p", { style: styles.hint, children: t('topKResultsHint') })] }), _jsxs("div", { style: styles.field, children: [_jsx("label", { style: styles.label, children: t('similarityThreshold') }), _jsx("input", { type: "number", value: similarityThreshold, onChange: (e) => setSimilarityThreshold(parseFloat(e.target.value) || 0.7), style: styles.input, min: 0, max: 1, step: 0.1, disabled: !ragEnabled }), _jsx("p", { style: styles.hint, children: t('similarityThresholdHint') })] })] })] }), _jsxs("div", { style: styles.footer, children: [_jsx("button", { style: styles.cancelButton, onClick: () => window.location.reload(), disabled: saving, children: t('cancel') }), _jsx("button", { style: saving ? styles.saveButtonDisabled : styles.saveButton, onClick: handleSaveSettings, disabled: saving, children: saving ? (_jsxs(_Fragment, { children: [_jsx(Loader2, { style: { ...styles.spinner, width: '16px', height: '16px' } }), t('saving')] })) : (_jsxs(_Fragment, { children: [_jsx(Save, { style: { width: '16px', height: '16px' } }), t('saveChanges')] })) })] })] }));
}
